import moment from 'moment';

import gameDataAPI from '../../../utils/gameDataAPI';
import landingApi from '../../../utils/landingApi';
import rightRailApi from '../../../utils/rightRailApi';
import boxscoreAPI from '../../../utils/boxscoreAPI';
import eventTypes from '../../../utils/eventTypes';
import gameStateTypes from '../../../utils/gameStateTypes';

import { isRequired } from '@nhl/param-helpers';
import { constants, dateTimeFormatter, stringFormatter } from '@nhl/formatter-utilities';

import actionCreators from './actionCreators';

const setRetainBoxscoreFeedDataId = actionCreators.setRetainBoxscoreFeedDataId;

const fetchGameDataByDateRange = (
    startDate = isRequired('startDate'),
    endDate = isRequired('endDate'),
    season = isRequired('season'),
    partyKey,
    retainBoxscoreFeedDataId,
    isHeaderData = false,
) => async dispatch => {
    try {
        await dispatch(actionCreators.requestGameData(partyKey));

        const data = await gameDataAPI.fetchGameData({
            startDate: startDate.format(constants.DATE_FORMAT),
            endDate: endDate.format(constants.DATE_FORMAT),
            season
        });

        if (isHeaderData) {
            dispatch(actionCreators.headerGameDataResponse(true, data.data, null, partyKey, retainBoxscoreFeedDataId));
        } else {
            dispatch(actionCreators.gameDataResponse(true, data.data, null, partyKey, retainBoxscoreFeedDataId));
        }
    } catch (error) {
        dispatch(actionCreators.gameDataResponse(false, [], error, partyKey))
    }
};

const purgeGameData = (partyKey) => actionCreators.purgeGameData(partyKey);

const fetchGameDataById = (gameId, partyKey, pastGameFallback = false) => async dispatch => {
    try {
        await dispatch(actionCreators.requestGameData(partyKey));

        const data = await gameDataAPI.fetchGameDataById({gameId});
        dispatch(actionCreators.gameDataResponse(true, [gameDataWithHFDRosters(data, pastGameFallback)], null, partyKey, gameId))
    } catch (error) {
        dispatch(actionCreators.gameDataResponse(false, [], error, partyKey))
    }
};

const fetchPastGameDataById = (gameId, partyKey) => async dispatch => {
    try {
        await dispatch(actionCreators.requestGameData(partyKey));

        const data = await gameDataAPI.fetchPastGameDataById({gameId});
        const gameData = data.data[0];
        // if we are attempting to fetch the data for a completed game but all the data is not yet available,
        // fall back to using HFD game data (this should only happen for recently finished games where the 400
        // hasn't pumped its data to the database - could be related to server side caching as well)
        if (gameData.goalieByGameStats.length > 0 && gameData.playerByGameStats.length > 0 && gameData.teamByGameStats.length > 0) {
            dispatch(actionCreators.gameDataResponse(true, [normalizePastGameData(data)], null, partyKey));
        } else {
            dispatch(fetchGameDataById(gameId, partyKey, true));
        }
    } catch (error) {
        dispatch(actionCreators.gameDataResponse(false, [], error, partyKey))
    }
};

const fetchPastOrLiveGameDataById = (gameId, partyKey, pastGameFallback = false) => async dispatch => {
    try {
        const { data } = await gameDataAPI.fetchGameStateDataById(gameId);
        if (data[0].state.id === gameStateTypes.OFFICIAL) {
            dispatch(fetchPastGameDataById(gameId, partyKey));
        } else {
            dispatch(fetchGameDataById(gameId, partyKey, pastGameFallback));
        }
    } catch (error) {
        dispatch(actionCreators.gameDataResponse(false, [], error, partyKey))
    }
};

const fetchXMostRecentGames = (gamesToFetch, partyKey, retainBoxscoreFeedDataId) => async dispatch => {
    try {
        await dispatch(actionCreators.requestGameData(partyKey));

        const data = await gameDataAPI.fetchXMostRecentGames({gamesToFetch});
        dispatch(actionCreators.gameDataResponse(true, data.data, null, partyKey, retainBoxscoreFeedDataId));
    } catch (error) {
        dispatch(actionCreators.gameDataResponse(false, [], error, partyKey))
    }
};

const fetchBoxscoreFeedDataById = (gameId, partyKey) => async dispatch => {
    try {
        await dispatch(actionCreators.requestBoxscoreFeedData(partyKey));

        const data = await boxscoreAPI.fetchBoxscoreFeedDataById({gameId});
        dispatch(actionCreators.boxscoreFeedDataResponse(true, [data], null, partyKey));
    } catch (error) {
        dispatch(actionCreators.boxscoreFeedDataResponse(false, [], error, partyKey))
    }
};



const fetchGameDate = partyKey => async dispatch => {
    try {
        await dispatch(actionCreators.requestGameDate(partyKey));

        const data = await gameDataAPI.fetchServerTime();
        const serverTime = moment(data.serverTime, constants.SQL_DATE_FORMAT);
        const gameDate = serverTime.hours() >= 12 ? serverTime : serverTime.clone().subtract(1, 'days');

        dispatch(actionCreators.gameDateResponse(true, gameDate, null, partyKey));
    } catch (error) {
        dispatch(actionCreators.gameDateResponse(false, [], error, partyKey))
    }
};

const fetchLandingData = gameId => async dispatch => {
    dispatch(actionCreators.requestLandingData());

    const landingData = await landingApi.fetchLandingData({gameId});
    dispatch(actionCreators.landingDataResponse(
        landingApi.getSummaryFromLandingData(landingData)
    ))
};

const resetLandingData = () => async dispatch => {
    dispatch(actionCreators.landingDataResponse(null, []));
};

const fetchRightRailData = gameId => async dispatch => {
    dispatch(actionCreators.requestRightRailData());

    const rightRailData = await rightRailApi.fetchRightRailData({gameId});
    dispatch(actionCreators.rightRailDataResponse(
        rightRailApi.getGameInfoFromRightRailData(rightRailData)
    ))
};

const resetRightRailData = () => async dispatch => {
    dispatch(actionCreators.rightRailDataResponse(null, []));
};

const gameDataWithHFDRosters = (data, pastGameFallback) => {
    const game = data.data[0];
    const goalieGameStatsMap = {};
    game.hfdGoalieGameStats.forEach((goalie) => {
        // To display goaltender stats correctly we only copy over goalie game stats if timeOnIceTotal exist.
        // However we always want to copy over the player info, so we can link out to the player page
        goalieGameStatsMap[goalie.playerId] = goalie.timeOnIceTotal === null
            ? {
                player: goalie?.player || {}
            } : goalie;
    });
    const skaterStats = game.hfdSkaterGameStats;
    const playerTOI = game.hfdPlayerTotalTimeOnIce;

    const normalizedRosterSpots = game.hfdRosterSpots.map(rosterSpot => {
        let playerStats;

        if (rosterSpot.positionCode === 'G') {
            playerStats = goalieGameStatsMap[rosterSpot.playerId];
        } else {
            playerStats = skaterStats.find(stats => stats.playerId === rosterSpot.playerId);
        }

        let spot = Object.assign(rosterSpot, playerStats);
        spot = Object.assign(spot, playerTOI.find(toi => toi.playerId === spot.playerId));

        spot.fullName = `${stringFormatter.capitalize(spot.firstName.toLowerCase())} ${stringFormatter.capitalize(spot.lastName.toLowerCase())}`;

        return spot;
    });

    // if we have fallen back from retrieving past game data to retrieving current game data, we need to UI to believe
    // we grabbed past game data, so we have to set this flag as we do in normalizePastGameData
    if (pastGameFallback) {
        game.isPastGameData = true;
    }

    return Object.assign(game, { normalizedRosterSpots });
};

const normalizePastGameData = data => {
    let normalizedData = Object.assign({}, data.data[0]);

    // move player and goalie data to hfdRosterSpots
    normalizedData.hfdRosterSpots = [...normalizedData.playerByGameStats, ...normalizedData.goalieByGameStats];
    delete normalizedData.playerByGameStats;
    delete normalizedData.goalieByGameStats;

    // add fake 'active' flag to roster spots, move teamId up, move sweaterNumber up, and move positionCode up
    normalizedData.hfdRosterSpots.forEach(rosterSpot => {
        rosterSpot.teamId = rosterSpot.team.id;

        rosterSpot.positionCode = rosterSpot.player && rosterSpot.player.position ? rosterSpot.player.position.positionCode : '';

        if (!rosterSpot.player || !rosterSpot.player.position) {
            console.log(rosterSpot.id);
        }

        rosterSpot.timeOnIceTotal = dateTimeFormatter.displaySecondsFormatted(rosterSpot.timeOnIceTotal || 0);
        rosterSpot.powerplayTimeOnIce = dateTimeFormatter.displaySecondsFormatted(rosterSpot.powerplayTimeOnIce || 0);
        rosterSpot.shorthandedTimeOnIce = dateTimeFormatter.displaySecondsFormatted(rosterSpot.shorthandedTimeOnIce || 0);
    });

    // move the stars data to hfdStars
    normalizedData.hfdStars = [];

    if (normalizedData.firstStar) {
        normalizedData.hfdStars.push(normalizedData.firstStar);
    }

    if (normalizedData.secondStar) {
        normalizedData.hfdStars.push(normalizedData.secondStar);
    }

    if (normalizedData.thirdStar) {
        normalizedData.hfdStars.push(normalizedData.thirdStar);
    }

    // sift through new roster array to find which teams these players belong to
    normalizedData.hfdStars = normalizedData.hfdStars.map(star => {
        const normalizedStar = {};
        const starsRosterSpot = normalizedData.hfdRosterSpots.find(rosterSpot => star.id === rosterSpot.player.id);

        normalizedStar.player = star;

        if (starsRosterSpot) {
            normalizedStar.player.sweaterNumber = starsRosterSpot.sweaterNumber;
            normalizedStar.team = starsRosterSpot.team;
        }

        return normalizedStar;
    });

    // remove unneeded keys
    delete normalizedData.firstStar;
    delete normalizedData.secondStar;
    delete normalizedData.thirdStar;

    // move the team stats data to hfdTeamGameStats
    normalizedData.hfdTeamGameStats = [
        ...normalizedData.teamByGameStats
    ];

    // remove unneeded keys
    delete normalizedData.teamGameByGameStats;

    // move goals to hfdEvents
    normalizedData.hfdEvents = [
        ...normalizedData.goals.map(goal => {
            const normalizedGoal = Object.assign({}, goal);

            normalizedGoal.player1Id = normalizedGoal.scoredBy;
            normalizedGoal.player1TeamId = normalizedGoal.scoringTeamId;
            normalizedGoal.player2Id = normalizedGoal.firstAssist;
            normalizedGoal.player2TeamId = normalizedGoal.scoringTeamId;
            normalizedGoal.player3Id = normalizedGoal.secondAssist;
            normalizedGoal.player3TeamId = normalizedGoal.scoringTeamId;
            normalizedGoal.player4Id = normalizedGoal.thirdAssist;
            normalizedGoal.player4TeamId = normalizedGoal.scoringTeamId;
            normalizedGoal.player5Id = normalizedGoal.fourthAssist;
            normalizedGoal.player5TeamId = normalizedGoal.scoringTeamId;
            normalizedGoal.typeCode = eventTypes.GOAL;
            normalizedGoal.timeInPeriod = normalizedGoal.timeOfGoal;
            normalizedGoal.eventId = normalizedGoal.eventId && Number(normalizedGoal.eventId.substring(3)); //stripping off the AS400 "tricode" from the eventId in the database.

            return normalizedGoal;

        }),
        ...normalizedData.shootoutDetails.map(shot => {
            const normalizedShootout = Object.assign({}, shot);

            normalizedShootout.period = 5;
            normalizedShootout.player1TeamId = normalizedShootout.shootingTeamId;
            normalizedShootout.typeCode = normalizedShootout.didScore === 'Y' ? eventTypes.GOAL : eventTypes.SHOT_ON_GOAL;
            normalizedShootout.eventId = normalizedShootout.eventId !== null
                ? Number(normalizedShootout.eventId.substring(3)) //stripping off the AS400 "tricode" from the eventId in the database.
                : null;

            return normalizedShootout;
        })
    ];

    delete normalizedData.goals;
    delete normalizedData.shootoutDetails;

    // move team period stats to hfdTeamPeriodStats
    normalizedData.hfdTeamPeriodStats = [...normalizedData.teamPeriodStats];
    delete normalizedData.teamPeriodStats;

    // flag for UI to know that we've fetched past data
    normalizedData.isPastGameData = true;

    return normalizedData;
};

export default {
    setRetainBoxscoreFeedDataId,
    fetchGameDataByDateRange,
    purgeGameData,
    fetchGameDataById,
    fetchXMostRecentGames,
    fetchPastGameDataById,
    fetchPastOrLiveGameDataById,
    fetchBoxscoreFeedDataById,
    fetchGameDate,
    fetchLandingData,
    resetLandingData,
    fetchRightRailData,
    resetRightRailData
};
